import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getCurrentUser, loginUser, logoutUser, registerUser, submitOnboardingIntent, updateLearningPaths } from '../api/authApi';
import { recordAnalyticsEvent } from '../api/analyticsApi';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCurrentUser()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const refreshUser = useCallback(async () => {
    const nextUser = await getCurrentUser().catch(() => null);
    setUser(nextUser);
    return nextUser;
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    refreshUser,
    register: async (details) => {
      recordAnalyticsEvent('signup_submitted', { email_domain: details.email?.split('@')[1] || '' });
      const nextUser = await registerUser(details);
      setUser(nextUser);
      recordAnalyticsEvent('signup_completed', { user_id: nextUser.id });
      return nextUser;
    },
    login: async (details) => {
      const nextUser = await loginUser(details);
      setUser(nextUser);
      recordAnalyticsEvent('login_completed', { user_id: nextUser.id });
      return nextUser;
    },
    updatePaths: async (learningPaths) => {
      const nextUser = await updateLearningPaths(learningPaths);
      setUser(nextUser);
      return nextUser;
    },
    submitIntent: async (intent) => {
      const nextUser = await submitOnboardingIntent(intent);
      setUser(nextUser);
      recordAnalyticsEvent('onboarding_intent_submitted', {
        interests: intent.interests || [],
        preferred_contact: intent.preferred_contact || 'email',
      });
      return nextUser;
    },
    logout: async () => {
      recordAnalyticsEvent('logout_clicked', { user_id: user?.id || '' });
      await logoutUser();
      setUser(null);
    }
  }), [loading, refreshUser, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
